import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Descriptions,
  Avatar,
  Tag,
  Space,
  Button,
  Typography,
  Tabs,
  Table,
  Image,
  Row,
  Col,
  Statistic,
  message,
  Spin,
  Empty,
  Popconfirm,
} from 'antd';
import {
  ArrowLeftOutlined,
  UserOutlined,
  EditOutlined,
  StopOutlined,
  UnlockOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { usersService, User } from '../services/usersService';
import { getImageUrl } from '../config/api';
import apiClient from '../services/apiClient';
import { Modal, Input, DatePicker, Select, Form, Switch } from 'antd';
import badgeService, { Badge, UserBadge } from '../services/badgeService';
import adminNoteService, { AdminNote } from '../services/adminNoteService';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

interface UserProfile {
  user: User;
  stats: {
    totalPosts: number;
    activePosts: number;
    inactivePosts: number;
    totalPets: number;
    availablePets: number;
    soldPets: number;
    followersCount: number;
    followingCount: number;
  };
  posts: {
    data: any[];
    pagination: any;
  };
  pets: any[];
}

const { TextArea } = Input;

const UserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [suspendModalVisible, setSuspendModalVisible] = useState(false);
  const [banModalVisible, setBanModalVisible] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [banReason, setBanReason] = useState('');
  const [suspendedUntil, setSuspendedUntil] = useState<string | null>(null);
  const [userBadges, setUserBadges] = useState<UserBadge[]>([]);
  const [badges, setBadges] = useState<Badge[]>([]);
  const [assignBadgeModalVisible, setAssignBadgeModalVisible] = useState(false);
  const [badgeForm] = Form.useForm();
  const [adminNotes, setAdminNotes] = useState<AdminNote[]>([]);
  const [noteModalVisible, setNoteModalVisible] = useState(false);
  const [editingNote, setEditingNote] = useState<AdminNote | null>(null);
  const [noteForm] = Form.useForm();

  useEffect(() => {
    if (id) {
      fetchUserProfile();
      loadUserBadges();
      loadBadges();
      loadAdminNotes();
    }
  }, [id]);

  const loadUserBadges = async () => {
    if (!id) return;
    try {
      const data = await badgeService.getUserBadges(id);
      setUserBadges(data);
    } catch (error: any) {
      console.error('Failed to load user badges:', error);
    }
  };

  const loadBadges = async () => {
    try {
      const data = await badgeService.getBadges();
      setBadges(data);
    } catch (error: any) {
      console.error('Failed to load badges:', error);
    }
  };

  const loadAdminNotes = async () => {
    if (!id) return;
    try {
      const data = await adminNoteService.getUserNotes(id);
      setAdminNotes(data);
    } catch (error: any) {
      console.error('Failed to load admin notes:', error);
    }
  };

  const handleCreateNote = () => {
    setEditingNote(null);
    noteForm.resetFields();
    setNoteModalVisible(true);
  };

  const handleEditNote = (note: AdminNote) => {
    setEditingNote(note);
    noteForm.setFieldsValue({
      note: note.note,
      tags: Array.isArray(note.tags) ? note.tags.join(', ') : (note.tags || ''),
      priority: note.priority || 'normal',
      isInternal: note.isInternal !== undefined ? note.isInternal : true,
    });
    setNoteModalVisible(true);
  };

  const handleSaveNote = async () => {
    try {
      const values = await noteForm.validateFields();
      if (!id) return;
      
      // Process tags - convert comma-separated string to array
      if (typeof values.tags === 'string') {
        values.tags = values.tags.split(',').map((tag: string) => tag.trim()).filter((tag: string) => tag.length > 0);
      } else if (!Array.isArray(values.tags)) {
        values.tags = [];
      }
      
      if (editingNote) {
        await adminNoteService.updateNote(editingNote._id, values);
        message.success('Note updated successfully');
      } else {
        await adminNoteService.createNote(id, values);
        message.success('Note created successfully');
      }
      
      setNoteModalVisible(false);
      loadAdminNotes();
    } catch (error: any) {
      if (error.errorFields) return;
      message.error('Failed to save note: ' + (error.message || 'Unknown error'));
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    try {
      await adminNoteService.deleteNote(noteId);
      message.success('Note deleted successfully');
      loadAdminNotes();
    } catch (error: any) {
      message.error('Failed to delete note: ' + (error.message || 'Unknown error'));
    }
  };

  const handleAssignBadge = async (values: any) => {
    if (!id) return;
    try {
      await badgeService.assignBadge(
        id,
        values.badgeId,
        values.expiresAt ? dayjs(values.expiresAt).toISOString() : null,
        values.notes
      );
      message.success('Badge assigned successfully');
      setAssignBadgeModalVisible(false);
      badgeForm.resetFields();
      loadUserBadges();
    } catch (error: any) {
      message.error(error.message || 'Failed to assign badge');
    }
  };

  const handleRemoveBadge = async (userBadgeId: string) => {
    try {
      await badgeService.removeBadge(userBadgeId);
      message.success('Badge removed successfully');
      loadUserBadges();
    } catch (error: any) {
      message.error(error.message || 'Failed to remove badge');
    }
  };

  const handleSuspend = async () => {
    try {
      await usersService.suspendUser(id!, suspendReason || undefined, suspendedUntil || undefined);
      message.success('User suspended successfully');
      setSuspendModalVisible(false);
      setSuspendReason('');
      setSuspendedUntil(null);
      fetchUserProfile();
    } catch (error: any) {
      message.error(error.message || 'Failed to suspend user');
    }
  };

  const handleBan = async () => {
    try {
      await usersService.banUser(id!, banReason || undefined);
      message.success('User banned successfully');
      setBanModalVisible(false);
      setBanReason('');
      fetchUserProfile();
    } catch (error: any) {
      message.error(error.message || 'Failed to ban user');
    }
  };

  const handleUnblock = async () => {
    try {
      await usersService.unblockUser(id!);
      message.success('User unblocked successfully');
      fetchUserProfile();
    } catch (error: any) {
      message.error(error.message || 'Failed to unblock user');
    }
  };

  const handleTogglePostingRestriction = async () => {
    try {
      await usersService.togglePostingRestriction(id!);
      message.success('Posting restriction toggled successfully');
      fetchUserProfile();
    } catch (error: any) {
      message.error(error.message || 'Failed to toggle posting restriction');
    }
  };

  const handleToggleMarketplaceRestriction = async () => {
    try {
      await usersService.toggleMarketplaceRestriction(id!);
      message.success('Marketplace restriction toggled successfully');
      fetchUserProfile();
    } catch (error: any) {
      message.error(error.message || 'Failed to toggle marketplace restriction');
    }
  };

  const handleBulkDisablePosts = async () => {
    try {
      const result = await usersService.bulkUserPostsAction(id!, 'disable');
      message.success(`Successfully disabled ${result.disabledCount || 0} posts`);
      fetchUserProfile();
    } catch (error: any) {
      message.error(error.message || 'Failed to disable posts');
    }
  };

  const handleBulkDeletePosts = async () => {
    try {
      const result = await usersService.bulkUserPostsAction(id!, 'delete');
      message.success(`Successfully deleted ${result.deletedCount || 0} posts permanently`);
      fetchUserProfile();
    } catch (error: any) {
      message.error(error.message || 'Failed to delete posts');
    }
  };

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/users/${id}/profile`);
      if (response.data.success) {
        setProfile(response.data.data);
      } else {
        message.error(response.data.message || 'Failed to fetch user profile');
      }
    } catch (error: any) {
      console.error('Error fetching user profile:', error);
      message.error(error.response?.data?.message || 'Failed to fetch user profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!profile) {
    return (
      <Card>
        <Empty description="User not found" />
      </Card>
    );
  }

  const { user, stats, posts, pets } = profile;

  const postColumns = [
    {
      title: 'Image',
      dataIndex: 'images',
      key: 'images',
      width: 100,
      render: (images: string[]) => (
        <Image
          src={images && images.length > 0 ? getImageUrl(images[0]) : ''}
          alt="Post"
          width={60}
          height={60}
          style={{ objectFit: 'cover', borderRadius: 4 }}
          fallback="https://via.placeholder.com/60x60"
        />
      ),
    },
    {
      title: 'Content',
      dataIndex: 'content',
      key: 'content',
      render: (content: string) => (
        <Text ellipsis={{ tooltip: content }} style={{ maxWidth: 300 }}>
          {content || '-'}
        </Text>
      ),
    },
    {
      title: 'Likes',
      dataIndex: 'likesCount',
      key: 'likesCount',
      width: 80,
    },
    {
      title: 'Comments',
      dataIndex: 'commentsCount',
      key: 'commentsCount',
      width: 100,
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: string) => date ? new Date(date).toLocaleDateString() : '-',
    },
  ];

  const petColumns = [
    {
      title: 'Image',
      dataIndex: 'images',
      key: 'images',
      width: 100,
      render: (images: string[]) => (
        <Image
          src={images && images.length > 0 ? getImageUrl(images[0]) : ''}
          alt="Pet"
          width={60}
          height={60}
          style={{ objectFit: 'cover', borderRadius: 4 }}
          fallback="https://via.placeholder.com/60x60"
        />
      ),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Category',
      dataIndex: 'petCategory',
      key: 'petCategory',
      render: (category: any) => category?.name || '-',
    },
    {
      title: 'Breed',
      dataIndex: 'breed',
      key: 'breed',
      render: (breed: any) => breed?.name || '-',
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (price: number) => price ? `₹${price.toLocaleString()}` : '-',
    },
    {
      title: 'Status',
      dataIndex: 'isAvailable',
      key: 'isAvailable',
      width: 100,
      render: (isAvailable: boolean) => (
        <Tag color={isAvailable ? 'green' : 'red'}>
          {isAvailable ? 'Available' : 'Sold'}
        </Tag>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/users')}
        style={{ marginBottom: 16 }}
      >
        Back to Users
      </Button>

      <Card>
        <Row gutter={[24, 24]}>
          <Col xs={24} md={8}>
            <div style={{ textAlign: 'center' }}>
              <Avatar
                src={getImageUrl(user.profileImage || '')}
                icon={<UserOutlined />}
                size={120}
                style={{ marginBottom: 16 }}
              />
              <Title level={3} style={{ marginBottom: 8 }}>
                {user.name}
              </Title>
              <Text type="secondary">@{user.username}</Text>
              <div style={{ marginTop: 16 }}>
                <Space wrap>
                  {user.isAdmin && <Tag color="purple">Admin</Tag>}
                  {user.isVerified && <Tag color="green">Verified</Tag>}
                  {user.isPrivate && <Tag color="orange">Private</Tag>}
                </Space>
              </div>
            </div>
          </Col>

          <Col xs={24} md={16}>
            <Descriptions title="User Information" bordered column={2}>
              <Descriptions.Item label="Email">
                {user.email || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Phone">
                {user.countryCode && user.phone ? `${user.countryCode}${user.phone}` : user.phone || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Bio" span={2}>
                {user.bio || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Address">
                {user.address || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Created">
                {user.createdOn ? new Date(user.createdOn).toLocaleDateString() : '-'}
              </Descriptions.Item>
            </Descriptions>

            <div style={{ marginTop: 24 }}>
              <Row gutter={16}>
                <Col xs={12} sm={6}>
                  <Statistic title="Posts" value={stats.totalPosts} />
                </Col>
                <Col xs={12} sm={6}>
                  <Statistic title="Pets" value={stats.totalPets} />
                </Col>
                <Col xs={12} sm={6}>
                  <Statistic title="Followers" value={stats.followersCount} />
                </Col>
                <Col xs={12} sm={6}>
                  <Statistic title="Following" value={stats.followingCount} />
                </Col>
              </Row>
            </div>

            <div style={{ marginTop: 24 }}>
              <Space wrap>
                <Button icon={<EditOutlined />} onClick={() => navigate(`/users/${id}/edit`)}>
                  Edit User
                </Button>
                {user.isBanned || user.isSuspended || !user.isActive ? (
                  <Popconfirm
                    title="Are you sure you want to unblock this user?"
                    onConfirm={handleUnblock}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button icon={<UnlockOutlined />} type="primary">
                      Unblock User
                    </Button>
                  </Popconfirm>
                ) : (
                  <>
                    <Button
                      icon={<StopOutlined />}
                      onClick={() => setSuspendModalVisible(true)}
                    >
                      Suspend User
                    </Button>
                    <Button
                      icon={<StopOutlined />}
                      danger
                      onClick={() => setBanModalVisible(true)}
                    >
                      Ban User
                    </Button>
                  </>
                )}
                <Popconfirm
                  title={`Are you sure you want to ${user.postingRestricted ? 'enable' : 'restrict'} posting for this user?`}
                  onConfirm={handleTogglePostingRestriction}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button>
                    {user.postingRestricted ? 'Enable Posting' : 'Restrict Posting'}
                  </Button>
                </Popconfirm>
                <Popconfirm
                  title={`Are you sure you want to ${user.marketplaceRestricted ? 'enable' : 'restrict'} marketplace access for this user?`}
                  onConfirm={handleToggleMarketplaceRestriction}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button>
                    {user.marketplaceRestricted ? 'Enable Marketplace' : 'Restrict Marketplace'}
                  </Button>
                </Popconfirm>
                <Popconfirm
                  title={`Are you sure you want to ${profile.stats.totalPosts > 0 ? 'disable all posts' : 'delete this user'}? This action cannot be undone.`}
                  onConfirm={() => {
                    if (profile.stats.totalPosts > 0) {
                      handleBulkDisablePosts();
                    } else {
                      message.info('Delete user feature coming soon');
                    }
                  }}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button icon={<DeleteOutlined />} danger>
                    {profile.stats.totalPosts > 0 ? 'Disable All Posts' : 'Delete User'}
                  </Button>
                </Popconfirm>
                {profile.stats.totalPosts > 0 && (
                  <Popconfirm
                    title="Are you sure you want to DELETE all posts permanently? This action cannot be undone and will remove all posts from the database."
                    onConfirm={handleBulkDeletePosts}
                    okText="Yes, Delete Permanently"
                    cancelText="Cancel"
                    okButtonProps={{ danger: true }}
                  >
                    <Button icon={<DeleteOutlined />} danger type="primary">
                      Delete All Posts
                    </Button>
                  </Popconfirm>
                )}
              </Space>
            </div>
          </Col>
        </Row>
      </Card>

      <Card style={{ marginTop: 24 }}>
        <Tabs
          defaultActiveKey="posts"
          items={[
            {
              key: 'posts',
              label: `Posts (${stats.totalPosts})`,
              children: (
                <Table
                  columns={postColumns}
                  dataSource={posts.data}
                  rowKey="_id"
                  pagination={{
                    current: posts.pagination.currentPage,
                    pageSize: posts.pagination.itemsPerPage,
                    total: posts.pagination.totalItems,
                    showSizeChanger: true,
                  }}
                />
              ),
            },
            {
              key: 'pets',
              label: `Pets (${stats.totalPets})`,
              children: (
                <Table
                  columns={petColumns}
                  dataSource={pets}
                  rowKey="_id"
                />
              ),
            },
            {
              key: 'badges',
              label: `Badges (${userBadges.length})`,
              children: (
                <div>
                  <div style={{ marginBottom: 16 }}>
                    <Button
                      type="primary"
                      onClick={() => setAssignBadgeModalVisible(true)}
                    >
                      Assign Badge
                    </Button>
                  </div>
                  {userBadges.length === 0 ? (
                    <Empty description="No badges assigned" />
                  ) : (
                    <Table
                      columns={[
                        {
                          title: 'Badge',
                          dataIndex: 'badge',
                          key: 'badge',
                          render: (badge: Badge) => (
                            <Tag color={badge.color || '#1890ff'}>{badge.name}</Tag>
                          ),
                        },
                        {
                          title: 'Description',
                          dataIndex: ['badge', 'description'],
                          key: 'description',
                        },
                        {
                          title: 'Assigned By',
                          dataIndex: ['assignedBy', 'name'],
                          key: 'assignedBy',
                        },
                        {
                          title: 'Assigned At',
                          dataIndex: 'assignedAt',
                          key: 'assignedAt',
                          render: (date: string) => new Date(date).toLocaleString(),
                        },
                        {
                          title: 'Expires At',
                          dataIndex: 'expiresAt',
                          key: 'expiresAt',
                          render: (date: string | null) => {
                            if (!date) return <Tag color="default">Never</Tag>;
                            const expiryDate = new Date(date);
                            const isExpired = expiryDate < new Date();
                            return (
                              <Tag color={isExpired ? 'red' : 'green'}>
                                {expiryDate.toLocaleString()}
                                {isExpired && ' (Expired)'}
                              </Tag>
                            );
                          },
                        },
                        {
                          title: 'Actions',
                          key: 'actions',
                          render: (_: any, record: UserBadge) => (
                            <Popconfirm
                              title="Are you sure you want to remove this badge?"
                              onConfirm={() => handleRemoveBadge(record._id)}
                              okText="Yes"
                              cancelText="No"
                            >
                              <Button type="link" danger size="small">
                                Remove
                              </Button>
                            </Popconfirm>
                          ),
                        },
                      ]}
                      dataSource={userBadges}
                      rowKey="_id"
                      pagination={false}
                    />
                  )}
                </div>
              ),
            },
            {
              key: 'notes',
              label: `Admin Notes (${adminNotes.length})`,
              children: (
                <div>
                  <div style={{ marginBottom: 16 }}>
                    <Button
                      type="primary"
                      onClick={handleCreateNote}
                    >
                      Add Note
                    </Button>
                  </div>
                  {adminNotes.length === 0 ? (
                    <Empty description="No admin notes" />
                  ) : (
                    <Table
                      columns={[
                        {
                          title: 'Note',
                          dataIndex: 'note',
                          key: 'note',
                          ellipsis: true,
                        },
                        {
                          title: 'Priority',
                          dataIndex: 'priority',
                          key: 'priority',
                          width: 100,
                          render: (priority: string) => {
                            const colors: Record<string, string> = {
                              low: 'default',
                              normal: 'blue',
                              high: 'orange',
                              urgent: 'red',
                            };
                            return <Tag color={colors[priority] || 'default'}>{priority?.toUpperCase() || 'NORMAL'}</Tag>;
                          },
                        },
                        {
                          title: 'Tags',
                          dataIndex: 'tags',
                          key: 'tags',
                          render: (tags: string[]) => (
                            <Space>
                              {tags?.map((tag, idx) => (
                                <Tag key={idx}>{tag}</Tag>
                              ))}
                            </Space>
                          ),
                        },
                        {
                          title: 'Created By',
                          dataIndex: ['admin', 'name'],
                          key: 'admin',
                          width: 150,
                        },
                        {
                          title: 'Created',
                          dataIndex: 'createdAt',
                          key: 'createdAt',
                          width: 150,
                          render: (date: string) => new Date(date).toLocaleString(),
                        },
                        {
                          title: 'Actions',
                          key: 'actions',
                          width: 120,
                          render: (_: any, record: AdminNote) => (
                            <Space>
                              <Button type="link" size="small" onClick={() => handleEditNote(record)}>
                                Edit
                              </Button>
                              <Popconfirm
                                title="Are you sure you want to delete this note?"
                                onConfirm={() => handleDeleteNote(record._id)}
                                okText="Yes"
                                cancelText="No"
                              >
                                <Button type="link" danger size="small">
                                  Delete
                                </Button>
                              </Popconfirm>
                            </Space>
                          ),
                        },
                      ]}
                      dataSource={adminNotes}
                      rowKey="_id"
                      pagination={false}
                    />
                  )}
                </div>
              ),
            },
            {
              key: 'stats',
              label: 'Statistics',
              children: (
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={6}>
                    <Card>
                      <Statistic title="Total Posts" value={stats.totalPosts} />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Card>
                      <Statistic title="Active Posts" value={stats.activePosts} valueStyle={{ color: '#3f8600' }} />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Card>
                      <Statistic title="Inactive Posts" value={stats.inactivePosts} valueStyle={{ color: '#cf1322' }} />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Card>
                      <Statistic title="Total Pets" value={stats.totalPets} />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Card>
                      <Statistic title="Available Pets" value={stats.availablePets} valueStyle={{ color: '#3f8600' }} />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Card>
                      <Statistic title="Sold Pets" value={stats.soldPets} valueStyle={{ color: '#cf1322' }} />
                    </Card>
                  </Col>
                </Row>
              ),
            },
          ]}
        />
      </Card>

      {/* Suspend Modal */}
      <Modal
        title="Suspend User"
        open={suspendModalVisible}
        onOk={handleSuspend}
        onCancel={() => {
          setSuspendModalVisible(false);
          setSuspendReason('');
          setSuspendedUntil(null);
        }}
        okText="Suspend"
        okButtonProps={{ danger: true }}
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong>Reason for suspension:</Text>
          <TextArea
            rows={4}
            value={suspendReason}
            onChange={(e) => setSuspendReason(e.target.value)}
            placeholder="Enter reason for suspension (optional)"
            style={{ marginTop: 8 }}
          />
        </div>
        <div>
          <Text strong>Suspended until (optional):</Text>
          <DatePicker
            showTime
            value={suspendedUntil ? new Date(suspendedUntil) : null}
            onChange={(date) => setSuspendedUntil(date ? date.toISOString() : null)}
            style={{ width: '100%', marginTop: 8 }}
            placeholder="Select date (default: 7 days)"
          />
        </div>
      </Modal>

      {/* Ban Modal */}
      <Modal
        title="Ban User Permanently"
        open={banModalVisible}
        onOk={handleBan}
        onCancel={() => {
          setBanModalVisible(false);
          setBanReason('');
        }}
        okText="Ban User"
        okButtonProps={{ danger: true }}
      >
        <div>
          <Text strong>Reason for ban:</Text>
          <TextArea
            rows={4}
            value={banReason}
            onChange={(e) => setBanReason(e.target.value)}
            placeholder="Enter reason for permanent ban (optional)"
            style={{ marginTop: 8 }}
          />
        </div>
      </Modal>

      {/* Admin Note Modal */}
      <Modal
        title={editingNote ? 'Edit Note' : 'Add Admin Note'}
        open={noteModalVisible}
        onOk={handleSaveNote}
        onCancel={() => {
          setNoteModalVisible(false);
          setEditingNote(null);
          noteForm.resetFields();
        }}
        width={600}
        okText="Save"
        cancelText="Cancel"
      >
        <Form
          form={noteForm}
          layout="vertical"
          initialValues={{
            priority: 'normal',
            isInternal: true,
            tags: [],
          }}
        >
          <Form.Item
            name="note"
            label="Note"
            rules={[{ required: true, message: 'Please enter a note' }]}
          >
            <TextArea rows={6} placeholder="Enter admin note (internal use only)" />
          </Form.Item>

          <Form.Item
            name="priority"
            label="Priority"
          >
            <Select>
              <Select.Option value="low">Low</Select.Option>
              <Select.Option value="normal">Normal</Select.Option>
              <Select.Option value="high">High</Select.Option>
              <Select.Option value="urgent">Urgent</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="tags"
            label="Tags"
            tooltip="Enter tags separated by commas"
            getValueProps={(value) => ({
              value: Array.isArray(value) ? value.join(', ') : (value || ''),
            })}
            normalize={(value) => {
              if (typeof value === 'string' && value.trim()) {
                return value.split(',').map((tag: string) => tag.trim()).filter((tag: string) => tag.length > 0);
              }
              return [];
            }}
          >
            <Input placeholder="e.g., warning, follow-up, important" />
          </Form.Item>

          <Form.Item
            name="isInternal"
            valuePropName="checked"
            label="Internal Note"
            tooltip="Internal notes are only visible to admins"
          >
            <Switch checkedChildren="Internal" unCheckedChildren="Visible to User" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserDetail;

